import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import FormDateWrapper from "../modals/CoreModal/FormDateWrapper";
import { FormInputWrapper } from "../modals/CoreModal/FormInputWrapper";
import {
  FormSelectWrapper,
  SelectOption,
} from "../modals/CoreModal/FormSelectWrapper";
import React from "react";
import styles from "./styles.module.scss";

interface ShipmentProduct {
  asin: string;
  sku: string;
  title: string;
  quantity: string;
  costPerUnit: string;
}

export interface ShipmentFormData {
  shipmentNumber: string;
  shipFromLocation: string;
  purchaseOrderNumber: string;
  status: string;
  issueDate: Date | null;
  freightMethod: string;
  shipmentDate: Date | null;
  estimatedDeliveryDate: Date | null;
  actualDeliveryDate: Date | null;
  destinationName: string;
  destinationCountry: string;
  products: ShipmentProduct[];
}

export interface ShipmentFormOptions {
  status?: SelectOption[];
  freight?: SelectOption[];
  purchaseOrder?: SelectOption[];
}

interface ShipmentFormProps {
  data: ShipmentFormData;
  onChange: (d: ShipmentFormData) => void;
  options?: ShipmentFormOptions;
}

const DEFAULT_STATUS: SelectOption[] = [
  { id: "Open", text: "Open" },
  { id: "Closed", text: "Closed" },
];
const DEFAULT_FREIGHT: SelectOption[] = [
  { id: "✈️ Air", text: "✈️ Air" },
  { id: "🚢 Sea", text: "🚢 Sea" },
  { id: "🚂 Train", text: "🚂 Train" },
];

export const ShipmentForm: React.FC<ShipmentFormProps> = ({
  data,
  onChange,
  options,
}) => {
  const setField = <K extends keyof ShipmentFormData>(
    field: K,
    value: ShipmentFormData[K],
  ) => onChange({ ...data, [field]: value });

  const handleProduct = (
    idx: number,
    field: keyof ShipmentProduct,
    val: string,
  ) => {
    const products = data.products.map((p, i) =>
      i === idx ? { ...p, [field]: val } : p,
    );
    onChange({ ...data, products });
  };

  const addProduct = () =>
    onChange({
      ...data,
      products: [
        ...data.products,
        { asin: "", sku: "", title: "", quantity: "", costPerUnit: "" },
      ],
    });

  const removeProduct = (idx: number) =>
    onChange({ ...data, products: data.products.filter((_, i) => i !== idx) });

  const statusOpts = options?.status ?? DEFAULT_STATUS;
  const freightOpts = options?.freight ?? DEFAULT_FREIGHT;
  const poOpts =
    options?.purchaseOrder ??
    Array.from(new Set(statusOpts.map((o) => o.id))).map((id) => ({
      id,
      text: id,
    }));

  return (
    <>
      <div className={styles.row}>
        <FormInputWrapper
          label="Shipment Number"
          placeholder="101"
          value={data.shipmentNumber}
          onChange={(v) => setField("shipmentNumber", v)}
        />
        <FormInputWrapper
          label="Ship-From Location"
          placeholder="Super supplement"
          value={data.shipFromLocation}
          onChange={(v) => setField("shipFromLocation", v)}
          optional
        />
      </div>

      <div className={styles.row}>
        <FormSelectWrapper
          label="Purchase Order Number"
          placeholder="Select …"
          value={data.purchaseOrderNumber}
          onChange={(v) => setField("purchaseOrderNumber", v)}
          options={poOpts}
        />
        <FormSelectWrapper
          label="Status"
          placeholder="Select …"
          value={data.status}
          onChange={(v) => setField("status", v)}
          options={statusOpts}
        />
      </div>

      <div className={styles.row}>
        <FormDateWrapper
          label="Issue Date"
          onChange={(v) => setField("issueDate", v)}
        />
        <FormSelectWrapper
          label="Freight Method"
          placeholder="Select …"
          value={data.freightMethod}
          onChange={(v) => setField("freightMethod", v)}
          options={freightOpts}
        />
      </div>

      <div className={styles.row}>
        <FormDateWrapper
          label="Shipment Date"
          onChange={(v) => setField("shipmentDate", v)}
        />
        <FormDateWrapper
          label="Estimated Delivery Date"
          optional
          onChange={(v) => setField("estimatedDeliveryDate", v)}
        />
        <FormDateWrapper
          label="Actual Delivery Date"
          optional
          onChange={(v) => setField("actualDeliveryDate", v)}
        />
      </div>

      <div className={styles.row}>
        <FormInputWrapper
          label="Destination Name"
          placeholder="Amazon UK main"
          value={data.destinationName}
          onChange={(v) => setField("destinationName", v)}
        />
        <FormInputWrapper
          label="Destination Country"
          placeholder="UK"
          value={data.destinationCountry}
          onChange={(v) => setField("destinationCountry", v)}
          optional
        />
      </div>

      <div className={styles.wrapperRow}>
        {data.products.map((product, index) => (
          <div key={`${product.asin}-${product.sku}`} className={styles.row}>
            <FormInputWrapper
              label={index === 0 ? "ASIN" : undefined}
              value={product.asin}
              onChange={(v) => handleProduct(index, "asin", v)}
            />
            <FormInputWrapper
              label={index === 0 ? "SKU" : undefined}
              value={product.sku}
              onChange={(v) => handleProduct(index, "sku", v)}
            />
            <FormInputWrapper
              label={index === 0 ? "Product Title" : undefined}
              value={product.title}
              onChange={(v) => handleProduct(index, "title", v)}
            />
            <FormInputWrapper
              label={index === 0 ? "Quantity" : undefined}
              type="number"
              value={product.quantity}
              onChange={(v) => handleProduct(index, "quantity", v)}
            />
            <FormInputWrapper
              label={index === 0 ? "Shipment Cost per Unit (£)" : undefined}
              value={product.costPerUnit}
              onChange={(v) => handleProduct(index, "costPerUnit", v)}
              optional
            />
            <button
              className={styles.removeBtn}
              onClick={() => removeProduct(index)}
            >
              <CloseOutlined />
            </button>
          </div>
        ))}
      </div>

      <button className={styles.action} onClick={addProduct}>
        <PlusOutlined /> <span>Add ASIN</span>
      </button>
    </>
  );
};
